import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as ActionCreators from '../../redux/action';
import { withRouter } from 'react-router-dom';
import ProfileContentSC, {
  InfoWrapSC,
  SettingsWrapSC,
  SettingsCardWrapSC,
  PersonalSettingsWrapSC,
  ApprovalSettingsWrapSC,
} from './profile-content.styled';
import { ReactComponent as UserBigIcon } from '../../assets/img/svg/profile/user-big.svg';
import { ReactComponent as NameIcon } from '../../assets/img/svg/profile/name.svg';
import { ReactComponent as EmailIcon } from '../../assets/img/svg/profile/email.svg';
import { ReactComponent as PhoneIcon } from '../../assets/img/svg/profile/phone.svg';
import { ReactComponent as PassIcon } from '../../assets/img/svg/profile/pass.svg';
import { ReactComponent as CheckIcon } from '../../assets/img/svg/profile/check.svg';
import Button from '../ui/button';
import { HeadingFour, SmallText, BiggerText } from '../typography';
import SettingsCard from './settings-card';
import BodyText from '../typography/body-text';
import { FormattedMessage, FormattedHTMLMessage } from 'react-intl';
import dlt from '../../utils/default-lang';
import WishmakerModal from '../ui/modal/modal';
import BorrowerDataChangeForm from './borrower-data-change-form';
import InvestorDataChangeForm from './investor-data-change-form';
import PersonalInfoChangeForm from './personal-info-change-form';
import SmsVerification from '../sms-verification';
import SuccessModal from '../success-modal';

const ProfileContent = ({ userData, sendSms, updateUserData, updateUserAgreements }) => {
  const [isDataChangeModalOpened, setIsDataChangeModalOpened] = useState(false);
  const [isAgreesUpdateModalOpened, setIsAgreesUpdateModalOpened] = useState(false);
  const [isSmsModalOpened, setSmsModalOpened] = useState(false);
  const [isSmsCodeError, setIsSmsCodeError] = useState(false);
  const [additionalData, setAdditionalData] = useState({});
  const [isSending, setIsSending] = useState(false);
  const [isSuccessModalOpened, setSuccessModalOpened] = useState(false);
  const isUserInvestor = userData && userData.type === 'investor';
  const [updateType, setUpdateType] = useState('');

  /* Send sms */
  const onSmsSend = (type) => {
    sendSms({ type });
  };

  const onEditClick = () => {
    setIsDataChangeModalOpened(true);
  };
  const onChangeDataFormSubmit = (values) => {
    onSmsSend('update-personal-info');
    setUpdateType('personal-info');
    setAdditionalData(values);
    setIsDataChangeModalOpened(false);
    setSmsModalOpened(true);
  };

  const onChangeAgreementsFormSubmit = (values) => {
    onSmsSend('update-agrees');
    setUpdateType('agreements');
    setAdditionalData(values);
    setIsAgreesUpdateModalOpened(false);
    setSmsModalOpened(true);
  };

  const onSmsModalSubmit = (smsModalData) => {
    const formattedData = {
      ...additionalData,
      sms_code: smsModalData.sms_code,
    };

    const onBadSmsCode = () => {
      setIsSending(false);
      setIsSmsCodeError(true);
    };
    const onSuccess = () => {
      setIsSending(false);
      setSmsModalOpened(false);
      setSuccessModalOpened(true);
      setAdditionalData({});
    };
    const onError = () => {
      setIsSending(false);
    };

    const editUserPersonalData = async () => {
      await updateUserData({
        data: formattedData,
        onSuccess,
        onBadSmsCode,
        onError,
      });
      setIsSending(true);
    };

    const editUserAgreements = async () => {
      await updateUserAgreements({
        data: formattedData,
        onSuccess,
        onBadSmsCode,
        onError,
      });
      setIsSending(true);
    };

    if (updateType === 'personal-info') {
      editUserPersonalData();
    } else if (updateType === 'agreements') {
      editUserAgreements();
    }
  };

  if (Object.keys(userData).length < 1) {
    return null;
  }

  return (
    <>
      <WishmakerModal
        isOpened={isDataChangeModalOpened}
        onClose={() => setIsDataChangeModalOpened(false)}
        className="special"
        withCross
      >
        <PersonalInfoChangeForm
          onSubmitForm={(values) => onChangeDataFormSubmit(values)}
          userData={userData}
        />
      </WishmakerModal>

      <WishmakerModal
        isOpened={isAgreesUpdateModalOpened}
        onClose={() => setIsAgreesUpdateModalOpened(false)}
        className="special"
        withCross
      >
        {isUserInvestor ? (
          <InvestorDataChangeForm
            onSubmitForm={(values) => onChangeAgreementsFormSubmit(values)}
            userData={userData}
          />
        ) : (
          <BorrowerDataChangeForm
            onSubmitForm={(values) => onChangeAgreementsFormSubmit(values)}
            userData={userData}
          />
        )}
      </WishmakerModal>

      <WishmakerModal
        isOpened={isSmsModalOpened}
        onClose={() => setSmsModalOpened(false)}
        className="special"
        withCross
      >
        <SmsVerification
          onSubmit={(data) => onSmsModalSubmit(data)}
          additionalData={additionalData}
          additionalInfo={
            updateType === 'personal-info'
              ? 'profile_personal_info_change_form_sms_confirm_additional_info'
              : 'profile_agreements_change_form_sms_confirm_additional_info'
          }
          isError={isSmsCodeError}
          isSending={isSending}
          onResend={() => onSmsSend()}
          onBadSms={() => setIsSmsCodeError(false)}
        />
      </WishmakerModal>

      <WishmakerModal
        isOpened={isSuccessModalOpened}
        onClose={() => setSuccessModalOpened(false)}
        className="special"
        withCross
      >
        <SuccessModal
          updateType={updateType}
          headlineText={
            updateType === 'personal-info'
              ? 'profile_personal_info_change_form_success_headline'
              : 'profile_agreements_change_form_success_headline'
          }
        />
      </WishmakerModal>

      <ProfileContentSC>
        <InfoWrapSC>
          <UserBigIcon />
          <HeadingFour>{userData.wm_number ? userData.wm_number : 'Username'}</HeadingFour>
          <BiggerText className="email-text">{userData.email}</BiggerText>
          <Button type="PRIMARY" size="MEDIUM" onClick={() => onEditClick()}>
            <FormattedMessage
              id="profile_edit_profile"
              defaultMessage={dlt['profile_edit_profile']}
            />
          </Button>
        </InfoWrapSC>
        <SettingsWrapSC>
          <SettingsCardWrapSC>
            <SettingsCard
              headline="profile_personal_info_headline"
              subText="profile_personal_info_subtext"
              onEditClick={() => onEditClick()}
            >
              <PersonalSettingsWrapSC>
                <div className="item">
                  <div className="item__img-wrap">
                    <NameIcon />
                  </div>
                  <div className="item__text-wrap">
                    <SmallText className="black-transparent">
                      <FormattedMessage
                        id="profile_full_name"
                        defaultMessage={dlt['profile_full_name']}
                      />
                    </SmallText>
                    <BiggerText className="data-text">
                      {userData.first_name && userData.last_name
                        ? `${userData.first_name} ${userData.last_name}`
                        : '-'}
                    </BiggerText>
                  </div>
                </div>
                <div className="item">
                  <div className="item__img-wrap">
                    <PhoneIcon />
                  </div>
                  <div className="item__text-wrap">
                    <SmallText className="black-transparent">
                      <FormattedMessage id="profile_phone" defaultMessage={dlt['profile_phone']} />
                    </SmallText>
                    <BiggerText className="data-text">
                      {userData.phone_number ? userData.phone_number : '-'}
                    </BiggerText>
                  </div>
                </div>
                <div className="item">
                  <div className="item__img-wrap">
                    <EmailIcon />
                  </div>
                  <div className="item__text-wrap">
                    <SmallText className="black-transparent">
                      <FormattedMessage id="profile_email" defaultMessage={dlt['profile_email']} />
                    </SmallText>
                    <BiggerText className="data-text">
                      {userData.email ? userData.email : '-'}
                    </BiggerText>
                  </div>
                </div>
                <div className="item">
                  <div className="item__img-wrap">
                    <PassIcon />
                  </div>
                  <div className="item__text-wrap">
                    <SmallText className="black-transparent">
                      <FormattedMessage
                        id="profile_password"
                        defaultMessage={dlt['profile_password']}
                      />
                    </SmallText>
                    <BiggerText className="data-text">*************</BiggerText>
                  </div>
                </div>
              </PersonalSettingsWrapSC>
            </SettingsCard>
          </SettingsCardWrapSC>
          <SettingsCardWrapSC>
            <SettingsCard
              headline="profile_approvals_headline"
              subText={isUserInvestor ? "profile_approvals_subtext_debtor" : "profile_approvals_subtext_debtor"}
              onEditClick={() => setIsAgreesUpdateModalOpened(true)}
            >

              <ApprovalSettingsWrapSC>
                <div className="item">
                  <div className="item__check-wrap">
                    {userData.personal_data_consent ? <CheckIcon /> : null}
                  </div>
                  <div className="item__text-wrap">
                    <BodyText className="uppercase">
                      <FormattedHTMLMessage
                        id="profile_approvals_first_headline"
                        defaultMessage={dlt['profile_approvals_first_headline']}
                      />
                    </BodyText>
                    <BodyText className="black-transparent">
                      <FormattedHTMLMessage
                        id="profile_approvals_first_subtext"
                        defaultMessage={dlt['profile_approvals_first_subtext']}
                      />
                    </BodyText>
                  </div>
                </div>

                <div className="item">
                  <div className="item__check-wrap">
                    {userData.marketing_personal_data_consent ? <CheckIcon /> : null}
                  </div>
                  <div className="item__text-wrap">
                    <BodyText className="uppercase">
                      <FormattedHTMLMessage
                        id="profile_approvals_second_headline"
                        defaultMessage={dlt['profile_approvals_second_headline']}
                      />
                    </BodyText>
                    <BodyText className="black-transparent">
                      <FormattedHTMLMessage
                        id="profile_approvals_second_subtext"
                        defaultMessage={dlt['profile_approvals_second_subtext']}
                      />
                    </BodyText>
                  </div>
                </div>
                {isUserInvestor ? (
                  <div className="item">
                    <div className="item__check-wrap">
                      {userData.consent_to_the_processing_of_personal_data_after_termination_of_the_contract ? (
                        <CheckIcon />
                      ) : null}
                    </div>
                    <div className="item__text-wrap">
                      <BodyText className="uppercase">
                        <FormattedHTMLMessage
                          id="profile_approvals_third_headline"
                          defaultMessage={dlt['profile_approvals_third_headline']}
                        />
                      </BodyText>
                      <BodyText className="black-transparent">
                        <FormattedHTMLMessage
                          id="profile_approvals_third_subtext"
                          defaultMessage={dlt['profile_approvals_third_subtext']}
                        />
                      </BodyText>
                    </div>
                  </div>
                ) : null}
              </ApprovalSettingsWrapSC>
            </SettingsCard>
          </SettingsCardWrapSC>
        </SettingsWrapSC>
      </ProfileContentSC>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    userData: state.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      sendSms: ActionCreators.sendSms,
      updateUserData: ActionCreators.updateUserData,
      updateUserAgreements: ActionCreators.updateUserAgreements,
    },
    dispatch
  );
};


export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ProfileContent));
